import React, { Fragment, useEffect } from 'react';
import { MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import Report from '@mui/icons-material/Report';
import CustomTable from '../tables/MuiDataGrid';
import { Eye } from 'react-feather';

const StockSummary = ({ history, location }) => {
  useEffect(() => {
    document.title = 'PFSA | Reports | Stock Summaries';
  }, []);
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    stock: [],
  });

  const [filterOptions, setFilterOptions] = React.useState({
    deliveryRunTypes: [],
    deliveryRuns: [],
  });
  const [searchInput, setSearchInput] = React.useState({
    id: 'All',
    delivery_run_type_id: 'All',
    planned_year: 'All',
  });

  const [path, setPath] = React.useState(new URLSearchParams(location.search).get('id'));

  React.useEffect(() => {
    if (path) {
      setSearchInput({ ...searchInput, id: path });
      setPath(null);
    }

    // Build query parameters from searchInput
    const queryParams = new URLSearchParams();
    if (searchInput.delivery_run_type_id !== 'All') {
      queryParams.append('delivery_run_type_id', searchInput.delivery_run_type_id);
    }
    if (searchInput.planned_year !== 'All') {
      queryParams.append('planned_year', searchInput.planned_year);
    }

    const queryString = queryParams.toString();
    const quickSearchUrl = `/delivery-runs/quick-search${queryString ? `?${queryString}` : ''}`;

    bulkAxios(['/delivery-run-types/names', quickSearchUrl])
      .then((all) => {
        setFilterOptions({
          deliveryRunTypes: all[0].deliveryRunTypeNames,
          deliveryRuns: all[1].deliveryRuns,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, [searchInput]);

  React.useEffect(() => {
    if (!path) {
      let queryString = buildQuery();

      setDataIsFetching(true);
      userRequest()
        .get(`/delivery-runs/stock?${queryString}`)
        .then(({ data }) => {
          console.log('data: ', data);
          setDataIsFetching(false);
          setTableData({
            count: data.total,
            stock: data.stock,
          });
        })
        .catch(({ response }) => {
          setDataIsFetching(false);
          console.log(response);
        });
    }
  }, [perPage, page, path, searchInput]);

  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'id') {
            if (value === 'All') {
              return [];
            } else {
              return `Delivery Run: ${value}`;
            }
          } else if (key === 'delivery_run_type_id') {
            if (value === 'All') {
              return [];
            } else {
              return `Delivery Run Type:${filterOptions.deliveryRunTypes.find((x) => x.id === value)?.display_name}`;
            }
          } else if (key === 'planned_year') {
            if (value === 'All') {
              return [];
            } else {
              return `Planned Deliery Start:${value === '>' ? 'Future' : value}`;
            }
          }
        }
      })
      .filter((x) => x !== undefined && x.length !== 0);
  }

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  const actionMethods = {};

  const columns = [
    {
      field: 'sku',
      headerName: 'SKU',
      flex: 1,
    },
    {
      field: 'qty',
      headerName: 'Quantity',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueGetter: ({ row }) => (row.qty ? formatNumber(row.qty ?? 0, 3) : 0),
    },
    {
      field: 'totalQty',
      headerName: 'Total Quantity',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueGetter: ({ row }) => (row.totalQty ? formatNumber(row.totalQty ?? 0, 3) + ' ' + (row.uom ?? '') : 0),
    },
  ];

  const onChangeInput = ({ target: t }) => {
    setSearchInput({ ...searchInput, [t.name]: t.value });
  };

  function buildQuery() {
    let queryString = Object.entries(searchInput)
      .map(([key, value]) => {
        // Check if the value is not empty or "All" before adding to the query
        if (value !== '' && value !== 'All' && value !== 0) {
          return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');

    return queryString;
  }

  const onSearch = (e) => {
    e.preventDefault();

    const queryString = buildQuery();

    userRequest()
      .get(`/delivery-runs/stock?${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          stock: data.stock,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };

  return (
    <Fragment>
      <Breadcrumb icon={<Report />} title="Stock Summary" parent="Reports" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>Planned Delivery Start</InputLabel>
                <Select
                  name="planned_year"
                  value={searchInput.planned_year}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Planned Delivery Start"
                  style={{ fontStyle: searchInput.planned_year === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={'>'}>Future Only</MenuItem>
                  {[
                    ...new Set(filterOptions.deliveryRuns.map((val) => val.planned_delivery_start_date?.slice(0, 4))),
                  ].map((val, i) => (
                    <MenuItem key={i} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>Delivery Run Type</InputLabel>
                <Select
                  name="delivery_run_type_id"
                  value={searchInput.delivery_run_type_id}
                  onChange={onChangeInput}
                  label="Delivery Run Type"
                  style={{ fontStyle: searchInput.delivery_run_type_id === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.deliveryRunTypes.map((val) => (
                    <MenuItem key={val.id} value={val.id}>
                      {val.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Delivery Run</InputLabel>
                <Select
                  name="id"
                  value={searchInput.id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Delivery Run"
                  style={{ fontStyle: searchInput.id === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.deliveryRuns.map((val, i) => (
                    <MenuItem key={i} value={val.id}>{`${val.display_name}: ${val.planned_delivery_start_date?.slice(
                      0,
                      10
                    )} (for ${val.consumption_start_date?.slice(0, 10)} to ${val.consumption_end_date?.slice(
                      0,
                      10
                    )})`}</MenuItem>
                  ))}
                  {}
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      <br />
      <br />
      <div className="container-fluid">
        <CustomTable
          rows={tableData.stock}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

function formatNumber(x) {
  var parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join('.');
}
export default StockSummary;
